import React from 'react';
import type { StatItem } from './api';

interface ORBDashboardProps {
  stats: StatItem[];
}

function pctCell(v: number | undefined | null, high = 60, mid = 40) {
  if (v == null) return <td style={styles.td}>—</td>;
  const color = v >= high ? '#22c55e' : v >= mid ? '#f59e0b' : '#ef4444';
  return <td style={{ ...styles.td, color }}>{v.toFixed(1)}%</td>;
}

function numCell(v: number | undefined | null) {
  if (v == null) return <td style={styles.td}>—</td>;
  return <td style={styles.td}>{v.toFixed(1)}R</td>;
}

export default function ORBDashboard({ stats }: ORBDashboardProps) {
  const tfColors: Record<string, string> = { m5: '#f59e0b', m15: '#3b82f6', m30: '#8b5cf6', h1: '#ec4899' };

  const rows = ['m5', 'm15', 'm30', 'h1'];

  return (
    <div>
      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>TF</th>
              <th style={styles.th}>Break ↑</th>
              <th style={styles.th}>Break ↓</th>
              <th style={styles.th}>1R</th>
              <th style={styles.th}>2R</th>
              <th style={styles.th}>3R</th>
              <th style={styles.th}>4R</th>
              <th style={styles.th}>5R</th>
              <th style={styles.th}>Avg↑</th>
              <th style={styles.th}>Avg↓</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(tf => {
              const found = stats.find(s => {
                const d = s.data as Record<string, unknown>;
                return (d as { timeframe?: string }).timeframe === tf || s.filter === `tf:${tf}`;
              });
              const d = found ? (found.data as Record<string, number>) : {};

              return (
                <tr key={tf} style={styles.tr}>
                  <td style={{ ...styles.td, color: tfColors[tf] ?? '#fff', fontWeight: 700 }}>
                    {tf?.toUpperCase()}
                  </td>
                  {pctCell(d.breakUpPct)}
                  {pctCell(d.breakDownPct)}
                  {pctCell(d.hitRate1R)}
                  {pctCell(d.hitRate2R)}
                  {pctCell(d.hitRate3R)}
                  {pctCell(d.hitRate4R)}
                  {pctCell(d.hitRate5R)}
                  {numCell(d.avgExtensionUp)}
                  {numCell(d.avgExtensionDown)}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={styles.note}>
        Break % = IB breakout probability | 1R–5R = hit rate at N× ORB range | Avg = avg extension beyond IB (in R)
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 12,
  },
  th: {
    textAlign: 'left',
    color: '#6b7280',
    fontWeight: 600,
    fontSize: 11,
    padding: '6px 10px',
    borderBottom: '1px solid rgba(245,158,11,0.12)',
    letterSpacing: '0.04em',
  },
  tr: {
    borderBottom: '1px solid rgba(245,158,11,0.06)',
  },
  td: {
    padding: '7px 10px',
    color: '#d4d0c8',
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: 12,
  },
  note: {
    fontSize: 11,
    color: '#475569',
    marginTop: 10,
    lineHeight: 1.5,
  },
};